import React from "react";
import Link from "next/link";
import Image from "next/image";
import type { ArticleMetadata } from "@/components/articles/ArticleMetadata.js";
import { cn } from "@rosette/ts/src/util/cn.js";


type RequiredMetadataFields = "fullTitle" | "path";
type RequiredArticleMetadata = Pick<Required<ArticleMetadata>, RequiredMetadataFields>;
type OptionalArticleMetadata = Exclude<Partial<ArticleMetadata>, RequiredMetadataFields>;
type ListArticleMetadata = RequiredArticleMetadata & OptionalArticleMetadata;


interface ArticleListProps {
    articles: ListArticleMetadata[];
    exclude?: ListArticleMetadata[];
    compact?: boolean;
}


export function ArticleList({ articles, exclude, compact }: ArticleListProps) {
    compact ??= false;

    const articleElems = [];
    for (const article of articles) {
        if (exclude && exclude.includes(article))
            continue;

        const title = article.shortTitle ?? article.fullTitle;
        articleElems.push(
            <div
                key={title.replaceAll(/[^a-zA-Z]/g, "")}
                className={cn(
                    "flex flex-col rounded-xl overflow-hidden bg-surface-neutral",
                    "border-b-4 border-b-surface-sunken",
                    compact ? null : "md:flex-row",
                )}>

                <Link href={article.path}>
                    {article.image && (
                        <Image
                            className={cn(
                                "object-cover h-full max-h-[10em]",
                                compact ? null : "md:max-h-none md:max-w-[16em]",
                            )}
                            src={article.image.src}
                            alt={article.image.alt} />
                    )}
                </Link>
                <div className="p-[1em]">
                    <Link className="text-body" href={article.path}>
                        <h3 className="mt-0 text-strong">{title}</h3>
                    </Link>
                    {article.date && (
                        <p className="mb-3 text-subtle">
                            {article.date}
                        </p>
                    )}
                    <p className="mb-0 text-body">
                        {article.htmlDescription ?? article.description}
                        {" "}
                        <Link href={article.path}>
                            Read more...
                        </Link>
                    </p>
                </div>
            </div>,
        );
    }
    return (
        <div className="flex flex-col gap-[2em]">
            {articleElems}
        </div>
    );
}
